import { reviewPillsAtom } from '@client/jotai/storefront/review-pills';
import AnalyticsConstants from '@constants/analytics';
import { MEDIA_QUERY_STATES } from '@constants/media-query';
import { useIsReviewPillsEligible } from '@hooks/use-review-pills';
import { useTrackVisible } from '@hooks/use-track-visible';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { useBaseStorefrontAnalytics } from '@hooks/useBaseStorefrontAnalytics';
import { useTrackStorefrontImpression } from '@hooks/useTrackStorefrontImpression';
import { useMedia } from '@tkww/orion-web-app-kernel';
import { Link } from '@tkww/orion-web-ds-action';
import { Icon } from '@tkww/orion-web-ds-graphic';
import { TextBody, TextTitle } from '@tkww/orion-web-ds-text';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import React, { useCallback, useMemo } from 'react';
import { scroller } from 'react-scroll';
import type { ReviewPill } from 'types/vendor';
import ReviewStars from '../../containers/vendor_info/components/review_stars';
import { ReviewFeaturesList } from './ReviewFeaturesList/ReviewFeaturesList';
import Styles from './ReviewSnippets.scss';
import { reviewSnippetsDataAtom } from './atoms';
import type { ReviewSnippet } from './types';

interface ReviewCardProps {
	snippet: ReviewSnippet;
}

interface SummaryCardProps {
	averageRating: number;
	totalReviewCount: number;
	pills: ReviewPill[] | null;
	featureListTrackingRef?: React.RefObject<HTMLElement>;
}

interface ReviewLinkProps {
	size: 'md' | 'lg';
	totalReviewCount: number;
}

const ReviewLink = ({ size, totalReviewCount }: ReviewLinkProps) => {
	const { track } = useAnalyticsContext();
	return (
		<Link
			as="button"
			treatAs="button"
			styleType="primary"
			size={size}
			data-testid="review-snippets-read-all-reviews"
			className={Styles.reviewLink}
			onClick={() => {
				track({
					event: 'CTA Clicked',
					properties: { action: 'review snippets' },
				});
				scroller.scrollTo('navReviews', { smooth: true, offset: -25 });
			}}
		>
			Read all reviews ({totalReviewCount})
		</Link>
	);
};

const SummaryCard = ({
	averageRating,
	totalReviewCount,
	pills,
	featureListTrackingRef,
}: SummaryCardProps) => {
	return (
		<article
			ref={featureListTrackingRef}
			data-testid="review-snippets-summary-card"
			className={`${Styles.card} ${Styles.summaryCard} ${
				pills ? Styles.summaryCardVariant1 : ''
			}`}
		>
			<div className={Styles.summaryTop}>
				<div className={Styles.summaryRateRow}>
					<ReviewStars
						overallRating={averageRating}
						size="md"
						wrapperClass={Styles.reviewStarsWrapper}
					/>
					<TextTitle size="sm">{averageRating.toFixed(1)}</TextTitle>
				</div>
				{!pills && (
					<div
						className={Styles.summaryVerifiedRow}
						data-testid="review-snippets-verified-row"
					>
						<Icon
							name="person-check"
							size="md"
							aria-hidden={true}
							aria-label={undefined}
						/>
						<TextBody size="sm">Verified reviews from real couples</TextBody>
					</div>
				)}
			</div>
			{pills ? (
				<ReviewFeaturesList pills={pills} />
			) : (
				<ReviewLink size="md" totalReviewCount={totalReviewCount} />
			)}
		</article>
	);
};

const ReviewCard = ({ snippet }: ReviewCardProps) => {
	const reviewerName = snippet.reviewerName || 'A couple';
	const isLg = useMedia('@lg') === MEDIA_QUERY_STATES.matched;
	const isXl = useMedia('@xl') === MEDIA_QUERY_STATES.matched;
	const isSingleStarView = isLg && !isXl;
	return (
		<article
			className={`${Styles.card} ${Styles.reviewCard}`}
			data-testid="review-snippets-card"
		>
			<TextBody
				size="sm"
				className={Styles.excerpt}
				data-testid="review-snippets-excerpt"
			>
				{snippet.excerpt}
			</TextBody>
			<div className={Styles.ratingRow}>
				<TextBody
					size="sm"
					bold
					className={Styles.reviewerName}
					data-testid="review-snippets-reviewer-name"
				>
					{reviewerName}
				</TextBody>
				<div
					className={Styles.starsAndRating}
					data-testid="review-snippets-card-rating"
				>
					{isSingleStarView ? (
						<Icon
							name="star-filled"
							size="sm"
							aria-label="Rating"
							color="rating-default"
						/>
					) : (
						<ReviewStars
							overallRating={snippet.rating}
							size="sm"
							wrapperClass={Styles.reviewStarsWrapper}
						/>
					)}
					<TextBody size="sm" bold>
						{snippet.rating.toFixed(1)}
					</TextBody>
				</div>
			</div>
		</article>
	);
};

export const ReviewSnippets = (): JSX.Element | null => {
	const data = useAtomValue(reviewSnippetsDataAtom);
	const pills = useAtomValue(reviewPillsAtom);
	const isReviewPillsEligible = useIsReviewPillsEligible();
	const showFeatureList = isReviewPillsEligible && pills != null;
	const isMobile = useMedia('@mobile') === MEDIA_QUERY_STATES.matched;
	const milestoneRef = useTrackStorefrontImpression('Review Snippets');
	const { track } = useAnalyticsContext();
	const baseStorefrontAnalytics = useBaseStorefrontAnalytics();

	const featureList = useMemo(
		() => (showFeatureList && pills ? pills.slice(0, 3) : []),
		[showFeatureList, pills],
	);

	const trackFeatureListVisible = useCallback(() => {
		if (featureList.length === 0) return;

		track({
			event: AnalyticsConstants.REVIEW_INTERACTION,
			properties: {
				...baseStorefrontAnalytics,
				selection: 'review pills',
				reviewPills: featureList.map(({ topicName }) => topicName),
			},
		});
	}, [baseStorefrontAnalytics, featureList, track]);

	const featureListTrackingRef = useTrackVisible(trackFeatureListVisible);

	if (!data) return null;

	const [snippet1, snippet2] = data.snippets;

	return (
		<section
			ref={milestoneRef}
			className={Styles.reviewSnippetsWrapper}
			aria-label="Review snippets"
			data-testid="review-snippets"
		>
			<div className={Styles.cardsAndCta}>
				<div
					className={classNames(Styles.cardsContainer, {
						[Styles.mobile]: isMobile,
					})}
					data-testid="review-snippets-cards-container"
				>
					<SummaryCard
						averageRating={data.averageRating}
						totalReviewCount={data.totalReviewCount}
						pills={showFeatureList ? pills : null}
						featureListTrackingRef={
							showFeatureList ? featureListTrackingRef : undefined
						}
					/>
					{snippet1 && <ReviewCard snippet={snippet1} />}
					{snippet2 && <ReviewCard snippet={snippet2} />}
				</div>
				{showFeatureList && (
					<ReviewLink size="lg" totalReviewCount={data.totalReviewCount} />
				)}
			</div>
		</section>
	);
};
